/**
 * WeatherCard (driver app)
 *
 * Shows the weather for a job's area — featuring the SCHEDULED day when known (so a driver
 * sees what to expect for their pickup/delivery), or current conditions otherwise. Tap to open
 * a detailed sheet with the HOURLY breakdown + 10-day outlook, like a weather app.
 *
 * Data: Open-Meteo (free, no key). Cached in-memory (rounded coords, 30-min TTL).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { RD, radii, spacing } from '../theme/colors';

type Daily = { date: string; code: number; hi: number; lo: number; precip: number; windMax: number };
type Hourly = { time: string; temp: number; code: number; precip: number };
type WX = { tempNow: number; codeNow: number; daily: Daily[]; hourly: Hourly[] };

const cache = new Map<string, { at: number; data: WX | null; p?: Promise<WX | null> }>();
const TTL = 30 * 60 * 1000;
const keyOf = (lat: number, lng: number) => `${lat.toFixed(2)},${lng.toFixed(2)}`;

async function fetchWX(lat: number, lng: number): Promise<WX | null> {
  const k = keyOf(lat, lng);
  const hit = cache.get(k);
  if (hit && Date.now() - hit.at < TTL) return hit.p ? hit.p : hit.data;
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
    `&current=temperature_2m,weather_code&hourly=temperature_2m,weather_code,precipitation_probability` +
    `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max` +
    `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=10`;
  const p = fetch(url).then(r => r.json()).then((j: any): WX => {
    const d = j.daily || {}, h = j.hourly || {};
    const data: WX = {
      tempNow: Math.round(j.current?.temperature_2m ?? 0),
      codeNow: j.current?.weather_code ?? 0,
      daily: (d.time || []).map((t: string, i: number) => ({ date: t, code: d.weather_code?.[i] ?? 0, hi: Math.round(d.temperature_2m_max?.[i] ?? 0), lo: Math.round(d.temperature_2m_min?.[i] ?? 0), precip: d.precipitation_probability_max?.[i] ?? 0, windMax: Math.round(d.wind_speed_10m_max?.[i] ?? 0) })),
      hourly: (h.time || []).map((t: string, i: number) => ({ time: t, temp: Math.round(h.temperature_2m?.[i] ?? 0), code: h.weather_code?.[i] ?? 0, precip: h.precipitation_probability?.[i] ?? 0 })),
    };
    cache.set(k, { at: Date.now(), data });
    return data;
  }).catch(() => { cache.set(k, { at: Date.now(), data: null }); return null; });
  cache.set(k, { at: Date.now(), data: null, p });
  return p;
}

function describe(code: number): { emoji: string; label: string } {
  if (code === 0) return { emoji: '☀️', label: 'Clear' };
  if (code === 1) return { emoji: '🌤️', label: 'Mostly clear' };
  if (code === 2) return { emoji: '⛅', label: 'Partly cloudy' };
  if (code === 3) return { emoji: '☁️', label: 'Overcast' };
  if (code === 45 || code === 48) return { emoji: '🌫️', label: 'Fog' };
  if (code >= 51 && code <= 57) return { emoji: '🌦️', label: 'Drizzle' };
  if (code >= 61 && code <= 67) return { emoji: '🌧️', label: 'Rain' };
  if (code >= 71 && code <= 77) return { emoji: '🌨️', label: 'Snow' };
  if (code >= 80 && code <= 82) return { emoji: '🌧️', label: 'Showers' };
  if (code === 85 || code === 86) return { emoji: '🌨️', label: 'Snow showers' };
  if (code >= 95) return { emoji: '⛈️', label: 'Thunderstorms' };
  return { emoji: '🌡️', label: '' };
}
const LIGHT = new Set([51, 56, 61, 66, 80, 71, 85]);
function dayBad(d: Daily): { bad: boolean; severe: boolean; reason: string } {
  if (d.code >= 95) return { bad: true, severe: true, reason: 'Thunderstorms' };
  if (d.code === 75 || d.code === 77 || d.code === 86) return { bad: true, severe: true, reason: 'Heavy snow' };
  if (d.windMax >= 30) return { bad: true, severe: d.windMax >= 45, reason: `Wind ${d.windMax} mph` };
  if (d.code >= 51 && d.code <= 86 && !LIGHT.has(d.code) && d.precip >= 70) return { bad: true, severe: false, reason: `${d.precip}% rain` };
  return { bad: false, severe: false, reason: '' };
}
const hourLabel = (iso: string) => { const h = Number(iso.slice(11, 13)); return `${((h + 11) % 12) + 1}${h >= 12 ? 'PM' : 'AM'}`; };
function dayLabel(dateStr: string, i: number) { if (i === 0) return 'Today'; if (i === 1) return 'Tomorrow'; return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }

export default function WeatherCard({ lat, lng, label, highlightDate }: { lat?: number | null; lng?: number | null; label?: string; highlightDate?: string }) {
  const [data, setData] = useState<WX | null>(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const ok = typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0) && !isNaN(lat) && !isNaN(lng);

  useEffect(() => {
    if (!ok) return; let live = true;
    fetchWX(lat as number, lng as number).then(d => { if (live) { setData(d); if (highlightDate && d) { const i = d.daily.findIndex(x => x.date === highlightDate); if (i >= 0) setSel(i); } } });
    return () => { live = false; };
  }, [ok, lat, lng, highlightDate]);

  if (!ok) return null;
  const featured = data ? (highlightDate ? data.daily.find(d => d.date === highlightDate) : data.daily[0]) || data.daily[0] : null;
  const showDay = !!highlightDate && !!featured;
  const desc = data ? describe(showDay ? featured!.code : data.codeNow) : null;
  const risk = featured ? dayBad(featured) : { bad: false, severe: false, reason: '' };
  const tempStr = data ? (showDay ? `${featured!.hi}°/${featured!.lo}°` : `${data.tempNow}°`) : '—';

  const day = data?.daily[sel];
  const dd = day ? describe(day.code) : { emoji: '🌡️', label: '' };
  const dRisk = day ? dayBad(day) : { bad: false, severe: false, reason: '' };
  const hours = data && day ? data.hourly.filter(h => h.time.slice(0, 10) === day.date) : [];

  return (
    <>
      <TouchableOpacity onPress={() => setOpen(true)} activeOpacity={0.8}
        style={[st.card, { backgroundColor: risk.bad ? (risk.severe ? '#FBEBE9' : '#FDF3E6') : '#EFF5FB', borderColor: risk.bad ? (risk.severe ? '#E6B4AE' : '#F0CE9B') : '#D5E4F2' }]}>
        <Text style={{ fontSize: 26 }}>{desc?.emoji || '🌡️'}</Text>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: '700', color: '#5b7088' }}>{label || 'Weather'}{showDay ? ` · ${dayLabel(featured!.date, data!.daily.indexOf(featured!))}` : ' · Now'}</Text>
          <Text style={{ fontSize: 15, fontWeight: '800', color: '#21303f' }}>{tempStr} <Text style={{ fontSize: 13, fontWeight: '600', color: '#52677c' }}>{desc?.label}</Text></Text>
          {risk.bad ? <Text style={{ fontSize: 12, fontWeight: '700', color: risk.severe ? '#9C2A22' : '#8a5a12' }}>⚠️ {risk.reason} — plan for it</Text>
            : featured && featured.precip > 0 ? <Text style={{ fontSize: 12, color: '#2E6FB8' }}>💧 {featured.precip}% rain</Text> : null}
        </View>
        <Text style={{ fontSize: 11, color: '#8aa0b6', fontWeight: '600' }}>Tap ›</Text>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        <View style={st.backdrop}>
          <View style={st.sheet}>
            <View style={st.sheetHead}>
              <Text style={st.sheetTitle}>{label || 'Weather'}</Text>
              <TouchableOpacity onPress={() => setOpen(false)} style={st.close}><Text style={{ fontSize: 18, color: '#5b7088' }}>✕</Text></TouchableOpacity>
            </View>
            {!data ? <ActivityIndicator style={{ marginVertical: 30 }} color="#630E3D" /> : (
              <ScrollView showsVerticalScrollIndicator={false}>
                {day && (
                  <View style={st.bigRow}>
                    <Text style={{ fontSize: 52 }}>{dd.emoji}</Text>
                    <View style={{ marginLeft: 14 }}>
                      <Text style={{ fontSize: 12, fontWeight: '700', color: '#5b7088' }}>{dayLabel(day.date, sel)}</Text>
                      <Text style={{ fontSize: 28, fontWeight: '800', color: '#21303f' }}>{day.hi}° <Text style={{ color: '#9fb0c0', fontSize: 20 }}>/ {day.lo}°</Text></Text>
                      <Text style={{ fontSize: 13, color: '#52677c', fontWeight: '600' }}>{dd.label}{day.precip > 0 ? ` · 💧 ${day.precip}%` : ''}{day.windMax ? ` · 💨 ${day.windMax}mph` : ''}</Text>
                    </View>
                  </View>
                )}
                {dRisk.bad && <View style={[st.warn, { backgroundColor: dRisk.severe ? '#FBEBE9' : '#FDF3E6', borderColor: dRisk.severe ? '#E6B4AE' : '#F0CE9B' }]}><Text style={{ fontSize: 12.5, fontWeight: '600', color: dRisk.severe ? '#9C2A22' : '#8a5a12' }}>⚠️ {dRisk.reason} — not ideal for an outdoor move.</Text></View>}
                <Text style={st.section}>HOURLY</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
                  {hours.map(h => { const hd = describe(h.code); return (
                    <View key={h.time} style={st.hourCell}>
                      <Text style={{ fontSize: 11, color: '#7589a0', fontWeight: '600' }}>{hourLabel(h.time)}</Text>
                      <Text style={{ fontSize: 18, marginVertical: 2 }}>{hd.emoji}</Text>
                      <Text style={{ fontSize: 13, fontWeight: '800', color: '#21303f' }}>{h.temp}°</Text>
                      <Text style={{ fontSize: 10, color: h.precip >= 40 ? '#2E6FB8' : '#b7c2cf', fontWeight: '600' }}>{h.precip > 0 ? `${h.precip}%` : ' '}</Text>
                    </View>
                  ); })}
                </ScrollView>
                <Text style={st.section}>10-DAY</Text>
                {data.daily.map((d, i) => { const de = describe(d.code); const r = dayBad(d); return (
                  <TouchableOpacity key={d.date} onPress={() => setSel(i)} style={[st.dayRow, { backgroundColor: i === sel ? '#eaf2fb' : 'transparent', borderColor: i === sel ? '#2E6FB8' : 'transparent' }]}>
                    <Text style={{ width: 92, fontSize: 13, fontWeight: '700', color: '#21303f' }}>{dayLabel(d.date, i)}</Text>
                    <Text style={{ fontSize: 18, width: 28, textAlign: 'center' }}>{de.emoji}</Text>
                    <Text style={{ flex: 1, fontSize: 12, color: r.bad ? (r.severe ? '#9C2A22' : '#8a5a12') : '#7589a0', fontWeight: r.bad ? '700' : '500' }}>{r.bad ? `⚠️ ${r.reason}` : de.label}</Text>
                    {d.precip > 0 ? <Text style={{ fontSize: 11.5, color: '#2E6FB8', width: 40, textAlign: 'right' }}>💧{d.precip}%</Text> : null}
                    <Text style={{ width: 74, textAlign: 'right', fontSize: 13.5, fontWeight: '800', color: '#21303f' }}>{d.hi}° <Text style={{ color: '#9fb0c0', fontWeight: '500' }}>/ {d.lo}°</Text></Text>
                  </TouchableOpacity>
                ); })}
                <Text style={{ fontSize: 10.5, color: '#aebccb', textAlign: 'right', marginTop: 8, marginBottom: 4 }}>Weather by Open-Meteo</Text>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
}

const st = StyleSheet.create({
  card: { flexDirection: 'row', alignItems: 'center', borderRadius: radii?.md ?? 12, borderWidth: 1, padding: 12, marginBottom: spacing?.sm ?? 10 },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 22, borderTopRightRadius: 22, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 26, maxHeight: '86%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  sheetTitle: { fontSize: 17, fontWeight: '800', color: '#21303f' },
  close: { width: 34, height: 34, borderRadius: 10, backgroundColor: '#F1ECE7', alignItems: 'center', justifyContent: 'center' },
  bigRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  warn: { borderWidth: 1, borderRadius: 11, padding: 10, marginBottom: 8 },
  section: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4, color: '#5b7088', marginTop: 10, marginBottom: 6 },
  hourCell: { width: 56, alignItems: 'center', paddingVertical: 9, marginRight: 5, borderRadius: 12, backgroundColor: '#F4F8FC', borderWidth: 1, borderColor: '#E2EBF3' },
  dayRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, paddingHorizontal: 8, borderRadius: 11, borderWidth: 1 },
});
